<script lang="ts">
  import { onMount, onDestroy, getContext } from 'svelte';
  import { Button } from '@lib/components';
  import { handleCtaNavigation } from '@lib/utils/navigation';
  import { getTranslations } from '$lib/interpreter';
  import type { GridBlock } from './types';
  import type { Writable } from 'svelte/store';

  // Browser check - works in both SSR and CSR
  const isBrowser = typeof window !== 'undefined';

  // Get translations
  let t: ReturnType<typeof getTranslations>['grid'];
  $: {
    if (isBrowser) {
      const surveyData = (window as any).__SURVEY_DATA__;
      const language = surveyData?.config?.language || 'en_us';
      const translations = getTranslations(language);
      t = translations.grid;
    }
  }

  export let block: GridBlock;
  export let onCtaClick: ((action?: string) => void) | undefined = undefined;

  // Get surveyId from context (provided by profile page)
  const surveyIdContext = getContext<Writable<string | null>>('surveyId');

  // Grid state
  let gridData: any = null;
  let loading = false;
  let error: string | null = null;

  // Width handling
  $: maxWidth = getMaxWidth();

  function getMaxWidth() {
    if (!block.layout?.width) return '800px';
    if (block.layout.width === 'full') return '100%';
    return `${block.layout.width}px`;
  }

  // Get normalized scores from sessionStorage
  function getNormalizedScores(): Record<string, number> {
    if (!isBrowser) return {};
    try {
      const raw = sessionStorage.getItem('walku:weights');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed?.normalizedScores || {};
    } catch {
      return {};
    }
  }

  // Get surveyId from JWT token, sessionStorage, or window
  function getSurveyId(): string | null {
    if (!isBrowser) return null;
    try {
      // Try extracting from JWT token first (works in fresh windows/incognito)
      const token = getProfileToken();
      if (token) {
        try {
          // Decode JWT payload (base64 decode the middle part)
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            if (payload.surveyId) {
              return payload.surveyId;
            }
          }
        } catch {
          // Failed to decode JWT, try other methods
        }
      }

      // Try sessionStorage (available during survey flow)
      const raw = sessionStorage.getItem('walku:survey');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.surveyId) {
          return parsed.surveyId;
        }
      }

      // Try window.__SURVEY_DATA__ (fallback)
      const surveyData = (window as any).__SURVEY_DATA__;
      if (surveyData?.config?.surveyId) {
        return surveyData.config.surveyId;
      }

      if (surveyData?.surveyId) {
        return surveyData.surveyId;
      }

      return null;
    } catch {
      return null;
    }
  }

  // Get profile token from sessionStorage (JWT for grid access)
  function getProfileToken(): string | null {
    if (!isBrowser) return null;
    try {
      const raw = sessionStorage.getItem('walku:profileToken');
      return raw || null;
    } catch {
      return null;
    }
  }

  // Get API base URL
  function getApiBaseUrl(): string {
    if (!isBrowser) return '';
    // Check for Vite environment variable first
    if (import.meta.env.VITE_FUNCTION_URL) {
      return import.meta.env.VITE_FUNCTION_URL;
    }
    // Fallback to window variable
    return (window as any).__FUNCTION_URL__ || 'http://localhost:3000';
  }

  // Get axis label from survey data
  function getAxisLabel(axisId: string): string {
    if (!isBrowser) return axisId;
    try {
      const surveyData = (window as any).__SURVEY_DATA__;
      if (surveyData && surveyData.axes) {
        const axis = surveyData.axes.find((a: any) => a.id === axisId);
        return axis?.label || axisId;
      }
    } catch {
      return axisId;
    }
    return axisId;
  }

  // Fetch grid data
  async function fetchGridData() {
    if (!isBrowser) return;

    const surveyId = getSurveyId();
    const normalizedScores = getNormalizedScores();
    const token = getProfileToken();

    if (!surveyId || !normalizedScores || Object.keys(normalizedScores).length === 0) {
      return;
    }

    if (!token) {
      error = block.errorMessage || 'Authentication token not found. Please complete the survey first.';
      return;
    }

    loading = true;
    error = null;

    try {
      const apiBase = getApiBaseUrl();
      const url = `${apiBase}/profile/grid`;

      const gridConfig = {
        xAxis: block.xAxis,
        yAxis: block.yAxis,
        cellsX: block.cellsX || 10,
        cellsY: block.cellsY || 10
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          surveyId,
          normalizedScores,
          gridConfig,
          token
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch grid data: ${response.status}`);
      }

      gridData = await response.json();
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to load grid';
    } finally {
      loading = false;
    }
  }

  // Simple: just fetch on mount, getSurveyId() handles finding the ID
  onMount(() => {
    if (isBrowser) {
      fetchGridData();
    }
  });

  // SVG dimensions
  const padding = 50;
  const svgWidth = 480;
  const svgHeight = 480;
  const gridWidth = svgWidth - padding * 2;
  const gridHeight = svgHeight - padding * 2;

  // Calculate cell dimensions
  $: cellWidth = gridWidth / (block.cellsX || 10);
  $: cellHeight = gridHeight / (block.cellsY || 10);

  // Map user position to SVG coordinates
  $: userX = gridData?.userScores?.[block.xAxis] ?? 50;
  $: userY = gridData?.userScores?.[block.yAxis] ?? 50;
  $: userSvgX = padding + (userX / 100) * gridWidth;
  $: userSvgY = padding + gridHeight - (userY / 100) * gridHeight; // Flip Y axis

  // Map cells to SVG coordinates
  $: mappedCells = (gridData?.gridCells || []).map((cell: any) => ({
    ...cell,
    svgX: padding + (cell.x[0] / 100) * gridWidth,
    svgY: padding + gridHeight - (cell.y[1] / 100) * gridHeight, // Flip Y axis
  }));

  // Map sample points to SVG coordinates with jitter
  $: mappedPoints = (gridData?.samplePoints || []).map((point: any) => {
    // Add random jitter (±3 pixels) to spread overlapping points
    const jitterX = (Math.random() - 0.5) * 6;
    const jitterY = (Math.random() - 0.5) * 6;

    return {
      x: point.x,
      y: point.y,
      svgX: padding + (point.x / 100) * gridWidth + jitterX,
      svgY: padding + gridHeight - (point.y / 100) * gridHeight + jitterY, // Flip Y axis
    };
  });

  // Use bubble visualization if we have sample points, otherwise fall back to cell grid
  $: useBubbles = mappedPoints.length > 0;

  // Axis ticks
  const ticks = [0, 25, 50, 75, 100];
</script>

<div class="grid-block" style:--max-width={maxWidth}>
  {#if loading}
    <div class="grid-loading">
      <p>{block.loadingMessage || (t ? t.loading : 'Loading distribution...')}</p>
    </div>
  {:else if error}
    <div class="grid-error">
      <p>{error}</p>
      {#if block.cta && block.cta.label}
        <div class="grid-cta">
          <Button
            label={block.cta.label}
            variant={block.cta.variant || 'primary'}
            size="lg"
            inline={true}
            on:click={() => handleCtaNavigation(block.cta, onCtaClick)}
          />
        </div>
      {/if}
    </div>
  {:else if gridData}
    {#if block.title || block.description}
      <div class="grid-header">
        {#if block.title}
          <h3 class="grid-title">{block.title}</h3>
        {/if}
        {#if block.description}
          <p class="grid-description">{block.description}</p>
        {/if}
      </div>
    {/if}

    <svg
      width="100%"
      height="100%"
      viewBox="0 0 {svgWidth} {svgHeight}"
      class="grid-svg"
      role="img"
      aria-label="Grid showing distribution of {gridData.totalResponses} responses"
    >
      <!-- Population visualization: bubbles or grid cells -->
      <g class="population">
        {#if useBubbles}
          <!-- Individual response bubbles -->
          {#each mappedPoints as point}
            <circle
              cx={point.svgX}
              cy={point.svgY}
              r="5"
              fill="var(--grid-cell-color, #3b82f6)"
              opacity="0.2"
              stroke="none"
              class="population-bubble"
            />
          {/each}
        {:else}
          <!-- Fallback: Grid cells -->
          {#each mappedCells as cell}
            <rect
              x={cell.svgX}
              y={cell.svgY}
              width={cellWidth}
              height={cellHeight}
              fill="var(--grid-cell-color, #3b82f6)"
              opacity={cell.count > 0 ? Math.max(0.1, Math.min(0.9, cell.percentage / 100)) : 0}
              stroke="none"
              class="grid-cell"
            >
              <title>{cell.count} {cell.count === 1 ? 'respuesta' : 'respuestas'} ({cell.percentage}%)</title>
            </rect>
          {/each}
        {/if}
      </g>

      <!-- Axis lines -->
      <g class="axes">
        <!-- X axis -->
        <line
          x1={padding}
          y1={padding + gridHeight}
          x2={padding + gridWidth}
          y2={padding + gridHeight}
          stroke="var(--grid-axis-color, #333)"
          stroke-width="2"
        />
        <!-- Y axis -->
        <line
          x1={padding}
          y1={padding}
          x2={padding}
          y2={padding + gridHeight}
          stroke="var(--grid-axis-color, #333)"
          stroke-width="2"
        />

        <!-- Tick marks and labels -->
        {#each ticks as tick}
          <!-- X axis ticks -->
          <line
            x1={padding + (tick / 100) * gridWidth}
            y1={padding + gridHeight}
            x2={padding + (tick / 100) * gridWidth}
            y2={padding + gridHeight + 5}
            stroke="var(--grid-axis-color, #333)"
            stroke-width="1"
          />
          <text
            x={padding + (tick / 100) * gridWidth}
            y={padding + gridHeight + 20}
            text-anchor="middle"
            font-size="12"
            fill="var(--grid-text-color, #666)"
          >
            {tick}
          </text>

          <!-- Y axis ticks -->
          <line
            x1={padding - 5}
            y1={padding + gridHeight - (tick / 100) * gridHeight}
            x2={padding}
            y2={padding + gridHeight - (tick / 100) * gridHeight}
            stroke="var(--grid-axis-color, #333)"
            stroke-width="1"
          />
          <text
            x={padding - 10}
            y={padding + gridHeight - (tick / 100) * gridHeight + 4}
            text-anchor="end"
            font-size="12"
            fill="var(--grid-text-color, #666)"
          >
            {tick}
          </text>
        {/each}
      </g>

      <!-- User position marker (colored dot) -->
      <g class="user-marker">
        <circle
          cx={userSvgX}
          cy={userSvgY}
          r="8"
          fill="var(--grid-user-color, #ff4444)"
          stroke="var(--grid-user-stroke, #fff)"
          stroke-width="2"
          class="user-dot"
        >
          <title>Tu posición</title>
        </circle>
        <circle
          cx={userSvgX}
          cy={userSvgY}
          r="12"
          fill="var(--grid-user-color, #ff4444)"
          opacity="0.3"
          class="user-pulse"
        />
      </g>

      <!-- Axis labels -->
      <text
        x={padding + gridWidth / 2}
        y={svgHeight - 10}
        text-anchor="middle"
        font-size="14"
        font-weight="600"
        fill="var(--grid-text-color, #333)"
      >
        {getAxisLabel(block.xAxis)} →
      </text>
      <text
        x="20"
        y={padding + gridHeight / 2}
        text-anchor="middle"
        font-size="14"
        font-weight="600"
        fill="var(--grid-text-color, #333)"
        transform="rotate(-90, 20, {padding + gridHeight / 2})"
      >
        {getAxisLabel(block.yAxis)} →
      </text>
    </svg>

    <div class="grid-footer">
      <p class="grid-stats">
        {#if t}
          {gridData.totalResponses === 1
            ? t.based_on_single.replace('{count}', gridData.totalResponses.toLocaleString())
            : t.based_on_multiple.replace('{count}', gridData.totalResponses.toLocaleString())
          }
        {:else}
          Based on {gridData.totalResponses.toLocaleString()} {gridData.totalResponses === 1 ? 'response' : 'responses'}
        {/if}
      </p>
    </div>
  {/if}
</div>

<style>
  .grid-block {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0;
    background: transparent;
    border-radius: 0;
    margin: 0;
    width: 100%;
    max-width: var(--max-width);
    margin-left: auto;
    margin-right: auto;
  }

  .grid-header {
    text-align: center;
    padding: 1rem 1rem 0 1rem;
  }

  .grid-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
    color: var(--grid-title-color, #222);
  }

  .grid-description {
    font-size: 1rem;
    color: var(--grid-description-color, #666);
    margin: 0;
  }

  .grid-svg {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
  }

  .grid-cell {
    transition: opacity 0.2s ease;
  }

  .grid-cell:hover {
    opacity: 1 !important;
  }

  .population-bubble {
    transition: opacity 0.2s ease, r 0.2s ease;
  }

  .population-bubble:hover {
    opacity: 0.4 !important;
    r: 4;
  }

  .user-dot {
    cursor: pointer;
  }

  .user-pulse {
    animation: pulse 2s ease-in-out infinite;
    transform-box: fill-box;
    transform-origin: center;
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 0.3;
      transform: scale(1);
    }
    50% {
      opacity: 0.6;
      transform: scale(1.1);
    }
  }

  .grid-footer {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    padding: 0 1rem 1rem 1rem;
  }

  .grid-stats {
    font-size: 0.875rem;
    color: var(--grid-stats-color, #666);
    margin: 0;
  }

  .grid-legend {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
  }

  .legend-label {
    color: var(--grid-legend-label, #666);
  }

  .legend-gradient {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .legend-low,
  .legend-high {
    color: var(--grid-legend-text, #888);
  }

  .gradient-bar {
    width: 100px;
    height: 16px;
    background: linear-gradient(to right,
      rgba(59, 130, 246, 0.1),
      rgba(59, 130, 246, 0.9)
    );
    border-radius: 4px;
    border: 1px solid var(--grid-border-color, #ddd);
  }

  .grid-loading,
  .grid-error {
    text-align: center;
    padding: 1rem;
    color: var(--grid-text-color, #666);
  }

  .grid-cta {
    margin-top: 1rem;
    display: flex;
    justify-content: center;
  }

  @media (max-width: 640px) {
    .grid-block {
      padding: 0;
      margin: 0;
    }

    .grid-title {
      font-size: 1.25rem;
    }
  }

  /* Dark mode support */
  :global([data-theme='dark']) .grid-block {
    background: var(--grid-background-dark, #1a1a1a);
  }

  :global([data-theme='dark']) .grid-title {
    color: var(--grid-title-color-dark, #f3f4f6);
  }

  :global([data-theme='dark']) .grid-description,
  :global([data-theme='dark']) .grid-stats,
  :global([data-theme='dark']) .legend-label {
    color: var(--grid-text-color-dark, #d1d5db);
  }
</style>

